/**
 * Rental Contract Configuration
 *
 * Comprehensive configuration for rental contract forms.
 * This configuration replaces the hardcoded AppleStyleRentalContractForm
 * with a declarative, maintainable structure.
 *
 * @module configs/contracts/rentalContract
 */

import { ContractFormConfig, FieldType, FieldConfig } from '../../types/contractForm.types';

/**
 * Rental Contract Form Configuration
 *
 * Defines the complete structure for rental contract forms including:
 * - 7 steps: Landlord Info, Tenant Info, Property Details, Rent & Payment, Rental Period, Additional Terms, Review
 * - 30+ fields with full validation
 * - Conditional logic for rental type-specific fields
 * - RAG integration for intelligent text field suggestions
 *
 * @constant
 */
export const rentalContractConfig: ContractFormConfig = {
  type: 'rental',
  subtype: undefined,

  metadata: {
    title: 'Nájemní smlouva',
    description: 'Konfigurace pro vytvoření nájemní smlouvy',
    version: '1.0.0',
    lastModified: new Date().toISOString(),
  },

  steps: [
    // ==================== STEP 0: Landlord Information ====================
    {
      title: 'Kdo pronajímá?',
      subtitle: 'Zadejte údaje o pronajímateli',
      description: 'Informace o majiteli nemovitosti, který ji pronajímá',
      icon: 'Home',
      color: 'success',
      fields: [
        {
          name: 'landlord_name',
          label: 'Jméno a příjmení / Název firmy',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Jméno pronajímatele je povinné' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. Jan Novák nebo ABC Reality s.r.o.',
          helpText: 'Zadejte celé jméno fyzické osoby nebo název firmy',
          examples: [
            'Jan Novák',
            'Marie Svobodová',
            'Reality Praha s.r.o.',
            'Bytový dům a.s.',
          ],
          ragEnabled: false,
        },
        {
          name: 'landlord_address',
          label: 'Adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa pronajímatele je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          examples: [
            'Václavské náměstí 1, 110 00 Praha 1',
            'Náměstí Svobody 10, 602 00 Brno',
            'Hlavní 25, 301 00 Plzeň',
          ],
          ragEnabled: false,
        },
        {
          name: 'landlord_id',
          label: 'Rodné číslo / IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nebo IČO je povinné' },
            {
              type: 'pattern',
              value: /^(\d{6}\/?\d{4}|\d{8})$/,
              message: 'Neplatný formát rodného čísla nebo IČO',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890 nebo 12345678',
          helpText: 'Rodné číslo pro fyzické osoby, IČO pro firmy',
          examples: ['123456/7890', '12345678'],
          ragEnabled: false,
        },
        {
          name: 'landlord_email',
          label: 'E-mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
          helpText: 'E-mail pro komunikaci ohledně nájmu',
          ragEnabled: false,
        },
        {
          name: 'landlord_business_id',
          label: 'DIČ (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'CZ12345678',
          helpText: 'Daňové identifikační číslo pro firmy',
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 1: Tenant Information ====================
    {
      title: 'Kdo si pronajme?',
      subtitle: 'Zadejte údaje o nájemníkovi',
      description: 'Informace o osobě, která si nemovitost pronajme',
      icon: 'Person',
      color: 'primary',
      fields: [
        {
          name: 'tenant_name',
          label: 'Jméno a příjmení',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Jméno nájemníka je povinné' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. Petra Nováková',
          examples: ['Petra Nováková', 'Tomáš Dvořák', 'Anna Svobodová'],
          ragEnabled: false,
        },
        {
          name: 'tenant_address',
          label: 'Současná adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Současná adresa nájemníka je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          helpText: 'Současné bydliště nájemníka',
          examples: [
            'Krásná 15, 120 00 Praha 2',
            'Dlouhá 8, 110 00 Praha 1',
          ],
          ragEnabled: false,
        },
        {
          name: 'tenant_id',
          label: 'Rodné číslo',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nájemníka je povinné' },
            {
              type: 'pattern',
              value: /^(\d{6}\/?\d{4})$/,
              message: 'Neplatný formát rodného čísla',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890',
          examples: ['123456/7890', '654321/1234'],
          ragEnabled: false,
        },
        {
          name: 'tenant_email',
          label: 'E-mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
          helpText: 'E-mail pro komunikaci ohledně nájmu',
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 2: Property Details ====================
    {
      title: 'Jaký byt/dům?',
      subtitle: 'Popište pronajímanou nemovitost',
      description: 'Detailní informace o předmětu nájmu',
      icon: 'LocationOn',
      color: 'warning',
      fields: [
        {
          name: 'property_description',
          label: 'Popis nemovitosti',
          type: FieldType.MULTILINE,
          rows: 3,
          validation: [
            { type: 'required', message: 'Popis nemovitosti je povinný' },
            {
              type: 'minLength',
              value: 20,
              message: 'Popis musí obsahovat alespoň 20 znaků',
            },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Detailně popište pronajímanou nemovitost...',
          examples: [
            'Byt 2+1, 55 m², 3. patro, výtah, balkon',
            'Rodinný dům 4+1, 120 m², zahrada 500 m²',
            'Garsonka 1+kk, 25 m², centrum města, klimatizace',
            'Kancelářské prostory 80 m², přízemí, parkování',
          ],
          helpText: 'Čím detailnější popis, tím lépe chráněny budou vaše práva',
          ragEnabled: true,
        },
        {
          name: 'property_address',
          label: 'Adresa nemovitosti',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa nemovitosti je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Přesná adresa pronajímané nemovitosti',
          examples: [
            'Wenceslas Square 1, 110 00 Prague 1',
            'Masaryk Street 15, 602 00 Brno',
          ],
          ragEnabled: false,
        },
        {
          name: 'property_energy_class',
          label: 'Energetická třída (volitelné)',
          type: FieldType.SELECT,
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'A', label: '🟢 A - Velmi úsporná' },
            { value: 'B', label: '🟢 B - Úsporná' },
            { value: 'C', label: '🟡 C - Vyhovující' },
            { value: 'D', label: '🟡 D - Méně úsporná' },
            { value: 'E', label: '🟠 E - Nehospodárná' },
            { value: 'F', label: '🔴 F - Velmi nehospodárná' },
            { value: 'G', label: '🔴 G - Mimořádně nehospodárná' },
          ],
          helpText: 'Energetická třída nemovitosti',
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 3: Rent and Payment ====================
    {
      title: 'Kolik a kdy?',
      subtitle: 'Finanční podmínky nájmu',
      description: 'Výše nájemného, kauce a platební podmínky',
      icon: 'Euro',
      color: 'error',
      fields: [
        {
          name: 'rent_amount',
          label: 'Měsíční nájem',
          type: FieldType.NUMBER,
          validation: [
            { type: 'required', message: 'Výše nájmu je povinná' },
            { type: 'min', value: 1, message: 'Nájem musí být větší než 0' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '15000',
          helpText: 'Zadejte částku v českých korunách',
          examples: ['15000', '25000', '35000'],
          min: 0,
          step: 100,
          ragEnabled: false,
        },
        {
          name: 'deposit_amount',
          label: 'Kauce',
          type: FieldType.NUMBER,
          validation: [
            { type: 'required', message: 'Výše kauce je povinná' },
            { type: 'min', value: 0, message: 'Kauce nemůže být záporná' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '45000',
          helpText: 'Obvykle 2-3 násobek měsíčního nájmu',
          examples: ['30000', '45000', '60000'],
          min: 0,
          step: 100,
          ragEnabled: false,
        },
        {
          name: 'payment_day',
          label: 'Den splatnosti nájmu (volitelné)',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: '1',
          helpText: 'Den v měsíci, kdy je nájem splatný (1-31)',
          examples: ['1', '15', '31'],
          min: 1,
          max: 31,
          step: 1,
          validation: [
            {
              type: 'min',
              value: 1,
              message: 'Den musí být mezi 1-31',
            },
            {
              type: 'max',
              value: 31,
              message: 'Den musí být mezi 1-31',
            },
          ],
          ragEnabled: false,
        },
        {
          name: 'utilities_included',
          label: 'Služby zahrnuty v nájmu',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Vodné, stočné, vytápění, elektřina, odvoz odpadu',
          defaultValue: false,
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 4: Rental Period ====================
    {
      title: 'Doba nájmu',
      subtitle: 'Kdy a na jak dlouho?',
      description: 'Doba trvání nájmu a typ nájemní smlouvy',
      icon: 'CalendarToday',
      color: 'secondary',
      fields: [
        {
          name: 'rental_type',
          label: 'Typ nájmu',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Typ nájmu je povinný' },
          ],
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'Dlouhodobý nájem', label: 'Dlouhodobý nájem', icon: '🏠' },
            { value: 'Krátkodobý nájem', label: 'Krátkodobý nájem', icon: '📅' },
            { value: 'Nájem na dobu určitou', label: 'Nájem na dobu určitou', icon: '⏰' },
            { value: 'Nájem na dobu neurčitou', label: 'Nájem na dobu neurčitou', icon: '♾️' },
          ],
          helpText: 'Vyberte typ nájemního vztahu',
          ragEnabled: false,
        },
        {
          name: 'start_date',
          label: 'Datum začátku nájmu',
          type: FieldType.DATE,
          validation: [
            { type: 'required', message: 'Datum začátku nájmu je povinné' },
          ],
          gridProps: { xs: 12, md: 6 },
          helpText: 'Kdy začíná nájem platit',
          ragEnabled: false,
        },
        {
          name: 'end_date',
          label: 'Datum konce nájmu',
          type: FieldType.DATE,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Kdy nájem končí',
          conditional: {
            field: 'rental_type',
            operator: 'equals',
            value: 'Nájem na dobu určitou',
            showWhenTrue: true,
          },
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 5: Additional Terms ====================
    {
      title: 'Dodatečné podmínky',
      subtitle: 'Pravidla a zvláštní podmínky nájmu',
      description: 'Volitelné podmínky a omezení týkající se nájmu',
      icon: 'Description',
      color: 'secondary',
      fields: [
        {
          name: 'pets_allowed',
          label: '🐕 Domácí zvířata povolena',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zda nájemce může chovat domácí zvířata',
          defaultValue: false,
          ragEnabled: false,
        },
        {
          name: 'smoking_allowed',
          label: '🚬 Kouření povoleno',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zda je v nemovitosti povoleno kouření',
          defaultValue: false,
          ragEnabled: false,
        },
        {
          name: 'parking_included',
          label: '🚗 Parkování zahrnuto',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zda je k nájmu zahrnuto parkování',
          defaultValue: false,
          ragEnabled: false,
        },
        {
          name: 'subletting_allowed',
          label: '🏡 Podnájem povolen',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zda nájemce může nemovitost podnajmout',
          defaultValue: false,
          ragEnabled: false,
        },
        {
          name: 'inflation_clause',
          label: '📈 Inflační doložka',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Automatické navyšování nájmu podle inflace',
          defaultValue: false,
          ragEnabled: false,
        },
        {
          name: 'special_conditions',
          label: 'Zvláštní podmínky (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Zde můžete uvést jakékoli dodatečné podmínky...',
          examples: [
            'Nájemník je povinen udržovat zahradu',
            'Zákaz hlučných akcí po 22:00',
            'Povinnost nahlásit delší nepřítomnost',
            'Pravidelné kontroly vlastníkem 1x ročně',
          ],
          helpText: 'Například pravidla pro používání, údržbu, apod.',
          ragEnabled: true,
        },
      ],
    },

    // Review step removed – preview is opened automatically in UI
  ],

  // ==================== Initial Values ====================
  initialValues: {
    // Default values for testing - remove in production
    // landlord_name: 'Pavel Dvořák',
    // landlord_address: 'Náměstí Míru 15, 120 00 Praha 2',
    // landlord_id: '6505150123',
    // landlord_email: 'pavel.dvorak@email.cz',
    // tenant_name: 'Anna Kratochvílová',
    // tenant_address: 'Wenceslas Square 10, 110 00 Praha 1',
    // tenant_id: '9055251234',
    // tenant_email: 'anna.kratochvilova@email.cz',
    // property_description: 'Byt 2+kk, 45 m², 3. patro, výtah, lodžie, částečně zařízený',
    // property_address: 'Vinohrady 789, 120 00 Praha 2',
    // property_energy_class: 'C',
    // rent_amount: 18000,
    // deposit_amount: 36000,
    // rental_type: 'Dlouhodobý nájem',
    // start_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    // end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    // payment_day: 1,
    // inflation_clause: true,
    // utilities_included: false,
    // parking_included: true,
    // pets_allowed: false,
    // smoking_allowed: false,
    // subletting_allowed: false,
    // special_conditions: 'Možnost prodloužení smlouvy, parkování v garáži zahrnuto',
  },

  // ==================== RAG Configuration ====================
  rag: {
    enabled: true,
    fields: [
      'property_description',
      'special_conditions',
    ],
    minConfidence: 0.7,
    maxSuggestions: 5,
    promptTemplate: `Analyzuj data nájemní smlouvy a poskytni inteligentní návrhy pro:
1. Úplnost popisu nemovitosti
2. Právní soulad a požadované klauzule
3. Jasnost dodatečných podmínek
4. Doporučení pro zmírnění rizik
5. Standardní ustanovení pro nájemní smlouvy`,
  },

  // ==================== Custom Validation ====================
  customValidation: (formData: any) => {
    const errors: Record<string, string> = {};

    // Validate deposit amount (typically 1-3x monthly rent)
    if (formData.deposit_amount && formData.rent_amount) {
      const depositRatio = formData.deposit_amount / formData.rent_amount;
      if (depositRatio > 6) {
        errors.deposit_amount =
          'Kauce je neobvykle vysoká. Běžná výše kauce je 2-3 násobek měsíčního nájmu.';
      }
    }

    // Validate rental period for fixed-term contracts
    if (formData.rental_type === 'Nájem na dobu určitou') {
      if (!formData.end_date) {
        errors.end_date = 'U nájmu na dobu určitou je datum konce povinné';
      } else if (formData.start_date) {
        const startDate = new Date(formData.start_date);
        const endDate = new Date(formData.end_date);

        if (endDate <= startDate) {
          errors.end_date = 'Datum konce musí být po datu začátku nájmu';
        }

        // Warn if rental period is very short (less than 1 month)
        const monthsDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
        if (monthsDiff < 1) {
          errors.end_date = 'Nájem na dobu kratší než měsíc je neobvyklý';
        }
      }
    }

    // Validate payment day
    if (formData.payment_day) {
      const day = Number(formData.payment_day);
      if (day < 1 || day > 31) {
        errors.payment_day = 'Den splatnosti musí být mezi 1 a 31';
      }
    }

    // Validate start date (should be in future or near past)
    if (formData.start_date) {
      const startDate = new Date(formData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      // Warn if start date is more than 3 months in the past
      if (daysDiff < -90) {
        errors.start_date = 'Datum začátku je více než 3 měsíce v minulosti';
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  },

  // ==================== Data Transformation ====================
  transformData: (formData: any) => {
    // Transform data before submission if needed
    const transformed = { ...formData };

    // Ensure numeric values are numbers
    if (transformed.rent_amount) {
      transformed.rent_amount = Number(transformed.rent_amount);
    }

    if (transformed.deposit_amount) {
      transformed.deposit_amount = Number(transformed.deposit_amount);
    }

    if (transformed.payment_day) {
      transformed.payment_day = Number(transformed.payment_day);
    }

    // Format dates to ISO string
    if (transformed.start_date) {
      transformed.start_date = new Date(transformed.start_date).toISOString().split('T')[0];
    }

    if (transformed.end_date) {
      transformed.end_date = new Date(transformed.end_date).toISOString().split('T')[0];
    }

    // Convert boolean values explicitly
    transformed.utilities_included = Boolean(transformed.utilities_included);
    transformed.pets_allowed = Boolean(transformed.pets_allowed);
    transformed.smoking_allowed = Boolean(transformed.smoking_allowed);
    transformed.parking_included = Boolean(transformed.parking_included);
    transformed.subletting_allowed = Boolean(transformed.subletting_allowed);
    transformed.inflation_clause = Boolean(transformed.inflation_clause);

    // Trim string values
    const stringFields = [
      'landlord_name',
      'landlord_address',
      'landlord_id',
      'landlord_email',
      'landlord_business_id',
      'tenant_name',
      'tenant_address',
      'tenant_id',
      'tenant_email',
      'property_description',
      'property_address',
      'property_energy_class',
      'rental_type',
      'special_conditions',
    ];

    stringFields.forEach((field) => {
      if (typeof transformed[field] === 'string') {
        transformed[field] = transformed[field].trim();
      }
    });

    return transformed;
  },
};

/**
 * Helper function to get step fields for validation
 *
 * Returns an array of field names that are required for a given step.
 * Used for step-by-step validation in multi-step forms.
 *
 * @param stepIndex - The step index (0-based)
 * @returns Array of required field names for the given step
 *
 * @example
 * ```typescript
 * const step0Fields = getRentalStepFields(0);
 * // Returns: ['landlord_name', 'landlord_address', 'landlord_id']
 *
 * const step2Fields = getRentalStepFields(2);
 * // Returns: ['property_description', 'property_address']
 * ```
 */
export const getRentalStepFields = (stepIndex: number): string[] => {
  if (stepIndex < 0 || stepIndex >= rentalContractConfig.steps.length) {
    return [];
  }

  return rentalContractConfig.steps[stepIndex].fields
    .filter((field: FieldConfig) =>
      field.validation?.some((rule: any) => rule.type === 'required')
    )
    .map((field: FieldConfig) => field.name);
};

/**
 * Helper function to validate rental-specific business rules
 *
 * @param formData - Complete form data
 * @returns Validation result with any errors
 *
 * @example
 * ```typescript
 * const result = validateRentalRules(formData);
 * if (!result.valid) {
 *   console.error('Validation errors:', result.errors);
 * }
 * ```
 */
export const validateRentalRules = (formData: any): { valid: boolean; errors?: string[] } => {
  const errors: string[] = [];

  // Check if deposit is reasonable
  if (formData.deposit_amount && formData.rent_amount) {
    const ratio = formData.deposit_amount / formData.rent_amount;
    if (ratio > 6) {
      errors.push('Kauce překračuje 6násobek měsíčního nájmu');
    }
  }

  // Check if rental period makes sense
  if (formData.start_date && formData.end_date) {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    if (end <= start) {
      errors.push('Datum konce musí být po datu začátku');
    }
  }

  return {
    valid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  };
};

/**
 * Helper function to get all field names from the rental contract config
 *
 * @returns Array of all field names in the configuration
 *
 * @example
 * ```typescript
 * const allFields = getRentalFieldNames();
 * // Returns: ['landlord_name', 'landlord_address', ..., 'special_conditions']
 * ```
 */
export const getRentalFieldNames = (): string[] => {
  return rentalContractConfig.steps.flatMap((step) =>
    step.fields.map((field) => field.name)
  );
};

/**
 * Helper function to get fields that support RAG
 *
 * @returns Array of field names that have RAG enabled
 *
 * @example
 * ```typescript
 * const ragFields = getRentalRAGFields();
 * // Returns: ['property_description', 'special_conditions']
 * ```
 */
export const getRentalRAGFields = (): string[] => {
  return rentalContractConfig.rag?.fields || [];
};

/**
 * Helper function to check if rental contract is ready for submission
 *
 * Validates that all required fields are present and valid.
 *
 * @param formData - Complete form data
 * @returns True if form is ready for submission
 *
 * @example
 * ```typescript
 * if (isRentalContractComplete(formData)) {
 *   await submitContract(formData);
 * }
 * ```
 */
export const isRentalContractComplete = (formData: any): boolean => {
  const requiredFields = [
    'landlord_name',
    'landlord_address',
    'landlord_id',
    'tenant_name',
    'tenant_address',
    'tenant_id',
    'property_description',
    'property_address',
    'rent_amount',
    'deposit_amount',
    'rental_type',
    'start_date',
  ];

  // Check if all required fields are present and not empty
  const allFieldsPresent = requiredFields.every((field) => {
    const value = formData[field];
    return value !== undefined && value !== null && value !== '';
  });

  if (!allFieldsPresent) {
    return false;
  }

  // Run custom validation
  const validation = rentalContractConfig.customValidation?.(formData);
  return validation?.valid ?? true;
};

export default rentalContractConfig;
